'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Car, KeyRound, CheckCircle2, CalendarCheck, Wallet,
  AlertTriangle, Plus, Users, LineChart, Clock, MapPin,
} from 'lucide-react';
import { z } from 'zod';
import type { Booking, FleetExpense, Vehicle } from '@/types';
import {
  daysUntil, complianceState, vidangeState,
} from '@/lib/morocco';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/currency';
import { deskDayRows, deskDaySchema, type DeskDayBoard, type DeskDayRow } from '@/lib/desk/day-board';
import { useDeskSummary } from '@/lib/desk/summary';
import { parseVehiclePage } from '@/lib/booking/laravel-mirror';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { StatusChip, bookingTone } from './desk-feedback';

interface DashboardModuleProps {
  vehicles: Vehicle[];
  bookings: Booking[];
  expenses: FleetExpense[];
  onGoto: (section: string) => void;
  currency?: string | null;
  headerQuery?: string;
  canViewProfits?: boolean;
  agencyName?: string;
  city?: string;
  onNewBooking?: () => void;
}

interface AlertRow {
  vehicleId: string;
  name: string;
  plate: string;
  label: string;
  days: number | null;
  overdue: boolean;
}

export function DashboardModule({
  vehicles: allVehicles,
  bookings: allBookings,
  expenses,
  onGoto,
  currency,
  headerQuery = '',
  canViewProfits = false,
  agencyName = '',
  city = '',
  onNewBooking,
}: DashboardModuleProps) {
  const needle = headerQuery.trim().toLowerCase();
  const vehicles = useMemo(
    () => (needle === '' ? allVehicles : allVehicles.filter((vehicle) => `${vehicle.name} ${vehicle.brand} ${vehicle.licensePlate}`.toLowerCase().includes(needle))),
    [allVehicles, needle],
  );
  const bookings = useMemo(
    () => (needle === '' ? allBookings : allBookings.filter((booking) => `${booking.id} ${booking.customerName} ${booking.vehicleName} ${booking.pickupLocation}`.toLowerCase().includes(needle))),
    [allBookings, needle],
  );
  const cur = currency ?? 'MAD';
  const limits = useDeskSummary();
  const attention = useTenantQuery(
    ['vehicles', 'attention'],
    '/api/staff/vehicles?attention=1&perPage=50',
    parseVehiclePage,
  );
  const finance = useTenantQuery(
    ['desk-finance', 'month'],
    '/api/desk/finance?period=month',
    (payload) => z.object({ expenses: z.number() }).parse(payload),
    canViewProfits,
  );
  const alertVehicles = (attention.data?.data as Vehicle[] | undefined) ?? vehicles;
  const today = new Date().toISOString().slice(0, 10);
  const monthKey = today.slice(0, 7);
  const [board, setBoard] = useState<DeskDayBoard | null>(null);
  const [boardFailed, setBoardFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/desk/day')
      .then((payload) => {
        if (!cancelled) setBoard(deskDaySchema.parse(payload));
      })
      .catch(() => {
        if (!cancelled) setBoardFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeRentals = limits.data?.summary.activeBookings
    ?? bookings.filter((b) => ['rented', 'active', 'reserved', 'confirmed'].includes(b.status)).length;
  const readyInLot = limits.data?.summary.availableVehicles
    ?? vehicles.filter((v) => v.status === 'available').length;
  const returnsToday = board?.returns.count ?? null;
  const revenueMonth = limits.data?.summary.revenueMonth
    ?? bookings
      .filter((b) => b.status !== 'cancelled' && (b.createdAt || b.pickupDate || '').slice(0, 7) === monthKey)
      .reduce((s, b) => s + (b.totalPrice || 0), 0);
  const expensesMonth = finance.data?.expenses
    ?? expenses.filter((e) => (e.date || '').slice(0, 7) === monthKey).reduce((s, e) => s + (e.amount || 0), 0);

  const alerts: AlertRow[] = useMemo(() => {
    const rows: AlertRow[] = [];
    for (const v of alertVehicles) {
      const checks: { label: string; date?: string }[] = [
        { label: 'Visite technique', date: v.visiteTechniqueExpiry },
        { label: 'Assurance', date: v.assuranceExpiry },
        { label: 'Vignette', date: v.vignetteExpiry },
      ];
      for (const c of checks) {
        const st = complianceState(c.date);
        if (st === 'expired' || st === 'soon') {
          rows.push({
            vehicleId: v.id, name: v.name, plate: v.licensePlate,
            label: c.label, days: daysUntil(c.date), overdue: st === 'expired',
          });
        } else if (st === 'missing') {
          rows.push({
            vehicleId: v.id, name: v.name, plate: v.licensePlate,
            label: `${c.label} missing`, days: null, overdue: true,
          });
        }
      }
      const vs = vidangeState(v);
      if (vs === 'expired' || vs === 'soon') {
        const remaining = (v.nextVidangeKm ?? 0) - v.mileage;
        rows.push({
          vehicleId: v.id, name: v.name, plate: v.licensePlate,
          label: `Vidange (${remaining.toLocaleString()} km left)`,
          days: null, overdue: vs === 'expired',
        });
      }
    }
    return rows.sort((a, b) => Number(b.overdue) - Number(a.overdue));
  }, [alertVehicles]);

  const pickupsToday = board ? deskDayRows(board.pickups, needle) : [];
  const dropoffsToday = board ? deskDayRows(board.returns, needle) : [];
  const overdueToday = board ? deskDayRows(board.overdue, needle) : [];
  const unpaidToday = board ? deskDayRows(board.unpaid, needle) : [];

  const topCars = useMemo(() => {
    const totals = new Map<string, { name: string; total: number }>();
    for (const b of bookings) {
      if (b.status === 'cancelled') continue;
      const prev = totals.get(b.vehicleId) ?? { name: b.vehicleName, total: 0 };
      totals.set(b.vehicleId, { name: b.vehicleName, total: prev.total + (b.totalPrice || 0) });
    }
    return [...totals.values()].sort((a, b) => b.total - a.total).slice(0, 5);
  }, [bookings]);
  const topMax = topCars[0]?.total ?? 1;

  const pendingCount = limits.data?.summary.pendingBookings ?? 0;
  const fleetTotal = limits.data?.vehicles ?? vehicles.length;
  const utilization = fleetTotal > 0 ? Math.round((activeRentals / fleetTotal) * 100) : 0;

  const kpis = [
    { label: 'Total Fleet', value: limits.data ? String(fleetTotal) : '…', sub: `${utilization}% utilized`, icon: Car, bg: 'bg-ink text-shell', delta: null as string | null, deltaUp: true },
    { label: 'Active Rentals', value: limits.data ? String(activeRentals) : '…', sub: pendingCount > 0 ? `${pendingCount} pending confirmation` : 'All confirmed', icon: KeyRound, bg: 'bg-brand/10 text-brand', delta: null, deltaUp: true },
    { label: 'Ready in Lot', value: limits.data ? String(readyInLot) : '…', sub: fleetTotal > 0 ? `${Math.round((readyInLot / fleetTotal) * 100)}% of fleet` : undefined, icon: CheckCircle2, bg: 'bg-success/10 text-success', delta: null, deltaUp: true },
    { label: 'Returns Today', value: returnsToday === null ? (boardFailed ? '—' : '…') : String(returnsToday), sub: undefined, icon: CalendarCheck, bg: 'bg-warning/10 text-warning', delta: null, deltaUp: true },
    { label: 'Revenue this month', value: limits.data ? formatMoney(revenueMonth, cur) : '…', sub: canViewProfits && finance.data ? `${formatMoney(expensesMonth, cur)} expenses` : undefined, icon: Wallet, bg: 'bg-[#FF8A00]/10 text-[#FF8A00]', delta: null, deltaUp: true },
  ];

  const deskName = agencyName.trim() || 'the desk';
  const pickupCount = board?.pickups.count ?? null;
  const returnCount = board?.returns.count ?? null;

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-3xl border border-line bg-card">
        <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(16rem,0.7fr)] lg:items-center">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand">
              {city.trim() || 'Today'}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
              Today at {deskName}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted">
              Pickups, returns, and documents that need a look before the counter gets busy.
            </p>
            <button
              type="button"
              onClick={() => (onNewBooking ? onNewBooking() : onGoto('bookings'))}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-xs font-semibold text-slate-950 transition-colors hover:brightness-110"
            >
              <Plus size={14} /> New booking
            </button>
          </div>
          <ul className="grid grid-cols-3 gap-2">
            <HeroStat label="Pickups" value={boardFailed ? '—' : pickupCount === null ? '…' : String(pickupCount)} />
            <HeroStat label="Returns" value={boardFailed ? '—' : returnCount === null ? '…' : String(returnCount)} />
            <HeroStat label="Alerts" value={String(alerts.length)} tone={alerts.length > 0 ? 'danger' : 'ok'} />
          </ul>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-2xl border border-line bg-card p-4 shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl ring-1 ring-inset ring-line ${k.bg}`}><k.icon size={17} /></span>
              {k.delta && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${k.deltaUp ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                  {k.delta}
                </span>
              )}
            </div>
            <p className="mt-3 text-lg font-bold tabular-nums text-ink">{k.value}</p>
            <p className="text-xs font-medium text-muted">{k.label}</p>
            {k.sub && <p className="mt-0.5 text-[11px] text-muted">{k.sub}</p>}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-line bg-card p-4 shadow-xs">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
            <AlertTriangle size={15} className="text-[#FF8A00]" /> Compliance alerts
            {alerts.length > 0 && (
              <span className="rounded-full border border-danger/30 bg-danger/10 px-2 py-0.5 text-[10px] font-bold tabular-nums text-danger">
                {alerts.length}
              </span>
            )}
          </h3>
          {alerts.length > 0 && (
            <button onClick={() => onGoto('maintenance')} className="text-[11px] font-bold text-[#FF8A00] hover:underline">
              View maintenance →
            </button>
          )}
        </div>
        {alerts.length === 0 ? (
          <p className="mt-2 flex items-center gap-2 rounded-xl bg-success/10 px-3 py-2 text-xs font-semibold text-success">
            <CheckCircle2 size={14} /> All documents compliant
          </p>
        ) : (
          <ul className="mt-2 max-h-56 space-y-1.5 overflow-y-auto">
            {alerts.map((a, i) => (
              <li key={`${a.vehicleId}-${a.label}-${i}`}
                onClick={() => onGoto('maintenance')}
                className={`flex cursor-pointer items-center justify-between gap-2 rounded-xl border px-3 py-2 text-xs transition-colors ${a.overdue ? 'border-danger/30 bg-danger/10' : 'border-warning/30 bg-warning/10'}`}>
                <div>
                  <p className="font-bold text-ink">{a.name} <span className="font-mono font-normal text-muted">· {a.plate}</span></p>
                  <p className={a.overdue ? 'text-danger' : 'text-warning'}>{a.label}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 font-bold tabular-nums ${a.overdue ? 'bg-danger text-white' : 'bg-warning text-slate-950'}`}>
                  {a.days === null ? (a.overdue ? 'Overdue' : 'Soon') : a.days < 0 ? `${Math.abs(a.days)}d overdue` : `${a.days}d left`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {boardFailed && (
        <p className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-xs font-semibold text-danger">
          Today's board could not be loaded.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <DayList
          title="Today's pickups"
          count={needle === '' ? board?.pickups.count ?? pickupsToday.length : pickupsToday.length}
          rows={pickupsToday}
          loading={board === null && !boardFailed}
          time={(row) => row.pickupTime}
          location={(row) => row.pickupLocation}
          onOpen={() => onGoto('handover')}
        />
        <DayList
          title="Today's returns"
          count={needle === '' ? board?.returns.count ?? dropoffsToday.length : dropoffsToday.length}
          rows={dropoffsToday}
          loading={board === null && !boardFailed}
          time={(row) => row.dropoffTime}
          location={(row) => row.dropoffLocation}
          onOpen={() => onGoto('handover')}
        />
        <DayList
          title="Overdue cars"
          count={needle === '' ? board?.overdue.count ?? overdueToday.length : overdueToday.length}
          rows={overdueToday}
          loading={board === null && !boardFailed}
          time={(row) => row.dropoffDate}
          location={(row) => row.dropoffLocation}
          onOpen={() => onGoto('handover')}
          empty="No car is still out past its return date."
        />
        <DayList
          title="Unpaid balances"
          count={needle === '' ? board?.unpaid.count ?? unpaidToday.length : unpaidToday.length}
          rows={unpaidToday}
          loading={board === null && !boardFailed}
          time={(row) => formatMoney(row.amountDue, cur)}
          location={(row) => row.dropoffDate}
          onOpen={() => onGoto('bookings')}
          empty="No balance due."
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-line bg-card p-4 shadow-xs">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-ink">Top profitable cars</h3>
            <button onClick={() => onGoto('analytics')} className="text-[11px] font-bold text-[#FF8A00] hover:underline">
              Full report →
            </button>
          </div>
          {topCars.length === 0 ? (
            <p className="mt-2 text-xs text-muted">No revenue yet.</p>
          ) : (
            <ul className="mt-3 space-y-2.5">
              {topCars.map((c, i) => (
                <li key={c.name}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-2 font-semibold text-ink">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black tabular-nums ${i === 0 ? 'bg-[#FF8A00] text-slate-950' : i === 1 ? 'bg-ink text-shell' : i === 2 ? 'bg-line text-ink' : 'bg-shell text-muted'}`}>
                        {i + 1}
                      </span>
                      <span className="truncate">{c.name}</span>
                    </span>
                    <span className="shrink-0 font-bold tabular-nums text-ink">{formatMoney(c.total, cur)}</span>
                  </div>
                  <div className="ml-7 h-2 overflow-hidden rounded-full bg-shell">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#FF8A00] to-amber-400 transition-all duration-500" style={{ width: `${Math.max(4, Math.round((c.total / topMax) * 100))}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-2xl border border-line bg-card p-4 shadow-xs">
          <h3 className="text-sm font-bold text-ink">Quick actions</h3>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button onClick={() => onGoto('bookings')} className="flex items-center gap-2 rounded-2xl bg-ink px-3 py-3 text-xs font-semibold text-shell transition-all hover:bg-brand hover:text-slate-950 active:scale-95">
              <Plus size={14} /> New booking
            </button>
            <button onClick={() => onGoto('fleet')} className="flex items-center gap-2 rounded-2xl border border-line bg-card px-3 py-3 text-xs font-semibold text-ink transition-all hover:border-ink hover:bg-shell active:scale-95">
              <Car size={14} /> Add vehicle
            </button>
            <button onClick={() => onGoto('customers')} className="flex items-center gap-2 rounded-2xl border border-line bg-card px-3 py-3 text-xs font-semibold text-ink transition-all hover:border-ink hover:bg-shell active:scale-95">
              <Users size={14} /> Clients
            </button>
            <button onClick={() => onGoto('analytics')} className="flex items-center gap-2 rounded-2xl border border-line bg-card px-3 py-3 text-xs font-semibold text-ink transition-all hover:border-ink hover:bg-shell active:scale-95">
              <LineChart size={14} /> Finance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'danger' | 'ok' }) {
  const valueClass = tone === 'danger' ? 'text-danger' : tone === 'ok' ? 'text-success' : 'text-ink';
  return (
    <li className="rounded-2xl border border-line bg-shell px-3 py-3">
      <p className={`text-xl font-semibold tabular-nums ${valueClass}`}>{value}</p>
      <p className="mt-1 text-[11px] font-medium text-muted">{label}</p>
    </li>
  );
}

function DayList({
  title,
  count,
  rows,
  loading,
  time,
  location,
  onOpen,
  empty = 'Nothing scheduled.',
}: {
  title: string;
  count: number;
  rows: DeskDayRow[];
  loading: boolean;
  time: (row: DeskDayRow) => string;
  location: (row: DeskDayRow) => string;
  onOpen: () => void;
  empty?: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-card p-4 shadow-xs">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-ink">{title} <span className="text-muted">({count})</span></h3>
        {rows.length > 0 && (
          <button type="button" onClick={onOpen} className="text-[11px] font-bold text-[#FF8A00] hover:underline">
            Open →
          </button>
        )}
      </div>
      {loading ? (
        <p className="mt-2 text-xs text-muted">Loading the day from the agency database…</p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-xs text-muted">{empty}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" onClick={onOpen} className="w-full cursor-pointer rounded-xl bg-shell px-3 py-2 text-left text-xs transition-colors hover:bg-brand/10">
                <p className="flex items-center justify-between gap-2 font-bold text-ink">
                  <span>{row.customerName} <span className="font-normal text-muted">· {row.vehicleName}</span></span>
                  <StatusChip tone={bookingTone(row.status)}>{row.status}</StatusChip>
                </p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted">
                  <span className="flex items-center gap-1"><Clock size={12} />{time(row) || '—'}</span>
                  <span className="flex items-center gap-1"><MapPin size={12} />{location(row)}</span>
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
